import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Check, 
  X, 
  Edit, 
  Trash2,
  Wallet,
  Scale,
  Laptop2,
  ShieldCheck,
  ClipboardList,
  Database,
  Users,
  Building2,
  Briefcase,
  UserCog,
  Brain,
  LineChart,
  Settings,
  FileText,
  UserCheck,
  Network,
  Globe,
  BookOpen,
  Microscope,
  Lightbulb,
  Target
} from 'lucide-react';
import { useTeamStore } from '../../stores/teamStore';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { ErrorMessage } from '../common/ErrorMessage';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { Role } from '../../types/user';
import { EditRoleModal } from './EditRoleModal';
import { getMenuItems } from '../layout/sidebar/menuItems';

// Role icon mapping
const roleIconMap = {
  'Financial Manager': Wallet,
  'Dispute Auditor': Scale,
  'IT Manager': Laptop2,
  'Admin': ShieldCheck,
  'Operations Manager': ClipboardList,
  'Data Manager': Database,
  'Organizers Supervisor': Users,
  'Facilities Manager': Building2,
  'HR Manager': Briefcase,
  'System Administrator': UserCog,
  'Strategic Planner': Brain,
  'Analytics Manager': LineChart,
  'Technical Supervisor': Settings,
  'Documentation Manager': FileText,
  'Compliance Officer': UserCheck,
  'Network Manager': Network,
  'International Relations': Globe,
  'Training Manager': BookOpen,
  'Quality Assurance': Microscope,
  'Innovation Manager': Lightbulb,
  'Project Manager': Target
};

export const RolesList = () => {
  const { roles, loading, error, deleteRole } = useTeamStore();
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    isOpen: boolean;
    role: Role | null;
  }>({
    isOpen: false,
    role: null
  });

  // Get menu items for admin role
  const menuItems = getMenuItems('admin');
  const flatMenuItems = Object.values(menuItems).flat();

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;

  return (
    <>
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-right text-sm font-medium text-gray-500 sticky right-0 bg-gray-50">
                  الدور
                </th>
                {flatMenuItems.map((item) => (
                  <th
                    key={item.path}
                    className="px-6 py-3 text-center text-sm font-medium text-gray-500 whitespace-nowrap"
                  >
                    <div className="flex flex-col items-center gap-2">
                      <item.icon size={16} />
                      <span>{item.label}</span>
                    </div>
                  </th>
                ))}
                <th className="px-6 py-3 text-center text-sm font-medium text-gray-500">
                  الإجراءات
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {Object.values(roles).map((role) => (
                <motion.tr
                  key={role.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="hover:bg-gray-50"
                >
                  {/* Role Info */}
                  <td className="px-6 py-4 sticky right-0 bg-white z-10">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-8 h-8 rounded-lg flex items-center justify-center"
                          style={{ 
                            backgroundColor: `${role.color}15`,
                            color: role.color
                          }}
                        >
                          {role.icon && roleIconMap[role.icon] ? 
                            React.createElement(roleIconMap[role.icon], { size: 18 }) : 
                            <ShieldCheck size={18} />}
                        </div>
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: role.color }}
                        />
                      </div>
                      <span className="font-medium">{role.name}</span>
                    </div>
                  </td>

                  {/* Permissions */}
                  {flatMenuItems.map((item) => (
                    <td key={item.path} className="px-6 py-4 text-center">
                      {role.menuItems[item.path] ? (
                        <Check className="mx-auto text-green-500" size={20} />
                      ) : (
                        <X className="mx-auto text-red-500" size={20} />
                      )}
                    </td>
                  ))}

                  {/* Actions */}
                  <td className="px-6 py-4 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={() => setEditingRole(role)}
                        className="p-2 text-primary-500 hover:bg-primary-50 rounded-lg transition-colors"
                      >
                        <Edit size={18} />
                      </button>
                      <button
                        onClick={() => setDeleteConfirm({ isOpen: true, role })}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Modal */}
      {editingRole && (
        <EditRoleModal
          isOpen={true}
          onClose={() => setEditingRole(null)}
          role={editingRole}
        />
      )}

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false, role: null })}
        onConfirm={() => {
          if (deleteConfirm.role) {
            deleteRole(deleteConfirm.role.id);
          }
          setDeleteConfirm({ isOpen: false, role: null });
        }}
        title="حذف الدور"
        message={`هل أنت متأكد من حذف دور "${deleteConfirm.role?.name}"؟`}
        type="danger"
        confirmText="حذف"
        cancelText="إلغاء"
      />
    </>
  );
};